/**
 * Maps Five9 lifecycle events into the API payloads used for reconciliation and transcript sync.
 */
import {
  FIVE9_CALL_API,
  five9CallPath,
  five9TranscriptPath
} from 'src/constants/five9CallApi';

const TERMINAL_STATES = new Set([
  'ENDED',
  'FINISHED',
  'DISCONNECTED',
  'REJECTED',
  'TERMINATED'
]);

const unwrap = (payload) => {
  let value = Array.isArray(payload) ? payload[0] : payload;
  if (value?.payload && typeof value.payload === 'object') value = value.payload;
  return value && typeof value === 'object' ? value : {};
};

const firstValue = (...values) => values.find((value) => value !== undefined
  && value !== null && value !== '');

const sourcesFor = (root) => [
  root.callData,
  root.interactionData,
  root.callLogData,
  root.interactionLogData,
  root
].filter(Boolean);

const readFrom = (sources, ...fields) => firstValue(...sources.flatMap(
  (source) => fields.map((field) => source?.[field])
));

const toIsoTimestamp = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = typeof value === 'number' ? value : Number.NaN;
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 1e12 ? numeric * 1000 : numeric)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const toNonNegativeInteger = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number) : undefined;
};

const toSecondsFromMillis = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number / 1000) : undefined;
};

const toText = (value) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (value && typeof value === 'object') {
    return firstValue(value.name, value.label, value.code, value.id);
  }
  return undefined;
};

const normalizeDirection = (callData) => {
  const raw = String(firstValue(
    callData?.direction,
    callData?.callDirection,
    callData?.callType
  ) || '').toUpperCase();
  if (raw.includes('OUTBOUND') || raw === 'AGENT') return 'OUTBOUND';
  if (raw.includes('INBOUND') || raw === 'CUSTOMER') return 'INBOUND';
  return undefined;
};

const buildIdentity = (root) => {
  const sources = sourcesFor(root);
  const read = (...fields) => readFrom(sources, ...fields);
  const five9SessionId = firstValue(
    read('sessionId'),
    read('five9SessionId')
  );
  const five9InteractionId = firstValue(
    read('interactionId'),
    read('five9InteractionId')
  );
  const five9CallId = firstValue(
    read('callId'),
    read('five9CallId')
  );
  if (!five9SessionId || !five9InteractionId || !five9CallId) return null;
  return {
    [FIVE9_CALL_API.sessionIdField]: String(five9SessionId),
    [FIVE9_CALL_API.interactionIdField]: String(five9InteractionId),
    [FIVE9_CALL_API.callIdField]: String(five9CallId)
  };
};

const compact = (value) => Object.keys(value).reduce((result, key) => {
  if (value[key] !== undefined && value[key] !== null && value[key] !== '') {
    result[key] = value[key];
  }
  return result;
}, {});

export const containsFive9CallPii = (value) => {
  if (!value || typeof value !== 'object') return false;
  const blocked = [
    /^(ani|dnis|number|contactDisplayName|raw|payload|transcript|summary)$/i,
    /^(recordingUrl|recordingUri|recordingReference)$/i
  ];
  return Object.keys(value).some((key) => (
    blocked.some((pattern) => pattern.test(key)) || containsFive9CallPii(value[key])
  ));
};

/**
 * Builds one or more backend operations from a Five9 call event payload.
 *
 * @param {string} eventType
 * @param {Record<string, any>} payload
 * @returns {Array<{ method: string, path: string, body: Record<string, any> }>}
 */
export const mapFive9CallEvent = (eventType, payload) => {
  const root = unwrap(payload);
  const identity = buildIdentity(root);
  if (!identity) return [];

  const callData = root.callData || root.interactionData || {};
  const sources = sourcesFor(root);
  const read = (...fields) => readFrom(sources, ...fields);
  const receivedAt = new Date().toISOString();
  const state = String(firstValue(
    root.callData?.callState,
    root.callData?.state,
    root.interactionData?.callState,
    root.interactionData?.state,
    root.callState,
    root.state
  ) || '').toUpperCase();
  const direction = normalizeDirection(callData);
  const isFinishedEvent = eventType === 'interaction.callFinished'
    || eventType === 'crm.saveLog';
  const isEndedEvent = eventType === 'interaction.callEnded'
    || (!isFinishedEvent && TERMINAL_STATES.has(state));
  const isAcceptedEvent = eventType === 'interaction.callAccepted'
    || (!isEndedEvent && !isFinishedEvent
      && eventType.startsWith('interaction.') && state === 'TALKING');
  const lifecycle = compact({
    [FIVE9_CALL_API.agentIdField]: toText(read('five9AgentId', 'agentId')),
    [FIVE9_CALL_API.aniField]: toText(read('ANI', 'ani', 'from')),
    [FIVE9_CALL_API.dnisField]: toText(read('DNIS', 'dnis', 'to')),
    [FIVE9_CALL_API.queueField]: toText(read('queue', 'queueName', 'skill', 'skillName')),
    [FIVE9_CALL_API.campaignField]: toText(read('campaign', 'campaignName')),
    [FIVE9_CALL_API.outcomeField]: toText(read('outcome', 'callOutcome', 'reason')),
    [FIVE9_CALL_API.recordingUrlField]: toText(read(
      'recordingUrl',
      'recordingURL',
      'recordingUri',
      'recordingReference'
    ))
  });

  const operations = [];
  if (isAcceptedEvent) {
    const startedAt = toIsoTimestamp(read(
      'callStartedAt',
      'startTime',
      'startedAt',
      'callStartTime'
    )) || receivedAt;
    const answeredAt = toIsoTimestamp(read(
      'callAnsweredAt',
      'answerTime',
      'answeredAt',
      'acceptedAt',
      'connectedAt'
    )) || receivedAt;
    operations.push({
      method: 'POST',
      path: FIVE9_CALL_API.callsPath,
      body: compact({
        ...identity,
        ...lifecycle,
        [FIVE9_CALL_API.callStateField]: state || 'ACCEPTED',
        [FIVE9_CALL_API.directionField]: direction,
        [FIVE9_CALL_API.callStartedAtField]: startedAt,
        [FIVE9_CALL_API.callAnsweredAtField]: answeredAt
      })
    });
  } else if (isEndedEvent || isFinishedEvent) {
    // five9SessionId comes from the route on PATCH; the API rejects it in the body.
    const { [FIVE9_CALL_API.sessionIdField]: _omitSessionId, ...patchIdentity } = identity;
    // `crm.saveLog` is the CRM wrap-up callback: it typically fires *after*
    // the real call-end event (often just to carry disposition/transcript)
    // and rarely repeats the original timestamps. Only genuine SDK terminal
    // events (`interaction.callEnded`/`interaction.callFinished`) default a
    // missing end time to "now" — defaulting it for `crm.saveLog` too would
    // overwrite the correct EndTime already stored by the earlier event.
    const isSaveLogEvent = eventType === 'crm.saveLog';
    const endedAt = toIsoTimestamp(read(
      'callEndedAt',
      'endTime',
      'endedAt',
      'callEndTime'
    )) || (isSaveLogEvent ? undefined : receivedAt);
    const providedTalkTime = firstValue(
      toNonNegativeInteger(read('talkTimeSeconds', 'talkDurationSeconds')),
      toSecondsFromMillis(read(
        'talkTimeMillis',
        'talkTimeMs',
        'talkDurationMillis',
        'talkTime'
      ))
    );
    const answeredAt = toIsoTimestamp(read(
      'callAnsweredAt',
      'answerTime',
      'answeredAt',
      'acceptedAt',
      'connectedAt'
    ));
    const startedAt = toIsoTimestamp(read(
      'callStartedAt',
      'startTime',
      'startedAt',
      'callStartTime'
    ));
    const derivedTalkTime = (answeredAt && endedAt)
      ? Math.max(0, Math.round((Date.parse(endedAt) - Date.parse(answeredAt)) / 1000))
      : undefined;
    // Guard against unit drift in SDK-supplied durations: talk time can never
    // exceed the call's own start-to-end span.
    const callSpanSeconds = (startedAt && endedAt)
      ? Math.max(0, Math.round((Date.parse(endedAt) - Date.parse(startedAt)) / 1000))
      : undefined;
    const plausibleTalkTime = (providedTalkTime !== undefined
      && callSpanSeconds !== undefined
      && providedTalkTime > callSpanSeconds)
      ? undefined
      : providedTalkTime;
    operations.push({
      method: 'PATCH',
      path: five9CallPath(identity.five9SessionId),
      body: compact({
        ...patchIdentity,
        ...lifecycle,
        [FIVE9_CALL_API.callStateField]: state || (isFinishedEvent ? 'FINISHED' : 'ENDED'),
        [FIVE9_CALL_API.callStartedAtField]: startedAt,
        [FIVE9_CALL_API.callAnsweredAtField]: answeredAt,
        [FIVE9_CALL_API.callEndedAtField]: endedAt,
        [FIVE9_CALL_API.talkTimeSecondsField]: plausibleTalkTime ?? derivedTalkTime,
        [FIVE9_CALL_API.dispositionField]: firstValue(
          root.callLogData?.dispositionName,
          root.callLogData?.disposition?.name,
          root.interactionLogData?.dispositionName,
          root.interactionLogData?.disposition?.name,
          root.dispositionName,
          typeof root.callLogData?.disposition === 'string'
            ? root.callLogData.disposition
            : undefined,
          typeof root.interactionLogData?.disposition === 'string'
            ? root.interactionLogData.disposition
            : undefined
        )
      })
    });
  }

  if (isFinishedEvent) {
    const transcript = firstValue(
      root.callLogData?.transcript,
      root.interactionLogData?.transcript,
      root.transcript
    );
    const summary = firstValue(
      root.callLogData?.summary,
      root.interactionLogData?.summary,
      root.summary
    );
    const transcriptReceivedAt = firstValue(
      root.callLogData?.transcriptReceivedAt,
      root.interactionLogData?.transcriptReceivedAt,
      root.transcriptReceivedAt
    );
    if (transcript) {
      operations.push({
        method: 'PUT',
        path: five9TranscriptPath(identity.five9SessionId),
        body: compact({
          [FIVE9_CALL_API.transcriptField]: transcript,
          [FIVE9_CALL_API.summaryField]: summary,
          [FIVE9_CALL_API.transcriptReceivedAtField]: transcriptReceivedAt
        })
      });
    }
  }
  return operations;
};

/**
 * Rebuilds the API operations for a persisted call during reconciliation.
 *
 * @param {import('src/type-defs/five9Types').Five9Call|null|undefined} call
 * @returns {Array<{ method: string, path: string, body: Record<string, any> }>}
 */
export const mapFive9CallForReconciliation = (call) => {
  if (!call) return [];
  const state = String(call.state || '').toUpperCase();
  let eventType = null;
  if (TERMINAL_STATES.has(state)) {
    eventType = 'interaction.callEnded';
  } else if (state === 'TALKING') {
    eventType = 'interaction.callAccepted';
  }
  if (!eventType) return [];
  // Reconciliation only has the reduced redux `call` object to work from
  // (extractCall() persists ids/ANI/DNIS/state/raw, but not
  // direction/timestamps/agent/queue/campaign). `extractCall()` also
  // preserves the original unwrapped SDK payload on `call.raw` — feed that
  // through as the callData source (specific identity/state fields still
  // win via the override below) so a reconciliation-triggered recreate
  // carries the same direction/timestamps/agent/queue/campaign a live
  // event would have sent, instead of only the bare identity+state.
  const rawRoot = (call.raw && typeof call.raw === 'object') ? call.raw : {};
  const rawSource = (rawRoot.callData && typeof rawRoot.callData === 'object' && rawRoot.callData)
    || (rawRoot.interactionData && typeof rawRoot.interactionData === 'object' && rawRoot.interactionData)
    || rawRoot;
  return mapFive9CallEvent(
    eventType,
    {
      callData: {
        ...rawSource,
        sessionId: call.five9SessionId || call.sessionId,
        interactionId: call.five9InteractionId || call.interactionId || call.callSid,
        callId: call.five9CallId || call.callId,
        callState: call.state,
        callType: call.direction || rawSource?.direction || rawSource?.callType,
        ANI: call.ani || rawSource?.ANI || rawSource?.ani,
        DNIS: call.dnis || rawSource?.DNIS || rawSource?.dnis
      }
    }
  );
};
